import {
  List as ImList,
  Map as ImMap,
  Record as ImRecord,
  type Set as ImSet,
  is,
  type OrderedMap,
  type RecordOf,
} from 'immutable';
import isEqual from 'lodash/isEqual';
import isEqualWith from 'lodash/isEqualWith';
import { DateTime, type Interval } from 'luxon';
import { createCachedSelector, FlatMapCache, LruMapCache, LruObjectCache } from 're-reselect';
import { createSelector, lruMemoize, type OutputSelector, weakMapMemoize } from 'reselect';

import {
  type ACCOUNTS_REDUCER_KEY,
  accountsSelectors,
  type accountsTypes,
  accountsUtils,
} from 'app-components/flux/accounts';
import { type CATEGORIES_REDUCER_KEY, categoriesSelectors } from 'app-components/flux/categories';
import { chartOfAccountsSelectors, chartOfAccountsTypes } from 'app-components/flux/chart-of-accounts';
import type { ChartOfAccountProps } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import { resourceChangeLog, selectorsHelpers } from 'app-components/flux/core';
import { getId } from 'app-components/flux/core/resourceStoreTypes';
import { dateSelectors } from 'app-components/flux/date';
import { type FEATURE_FLAGS_REDUCER_KEY, featureFlagsSelectors } from 'app-components/flux/feature-flags';
import type * as filtersTypes from 'app-components/flux/filters/filtersTypes';
import { investmentHoldingsV2Selectors } from 'app-components/flux/investment-holdings-v2';
import { preferencesV2Selectors } from 'app-components/flux/preferences-v2';
import type { PREFERENCES_V2_REDUCER_KEY } from 'app-components/flux/preferences-v2/preferencesV2Types';
import type { ScheduledTransactionType } from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import { SECURITIES_REDUCER_KEY } from 'app-components/flux/securities/securitiesTypes';
import { tagsSelectors } from 'app-components/flux/tags';
import { getLogger } from 'app-components/utils/core';
import {
  createWeakMapSelector,
  immutableEntriesShallowEqual,
  resettableLruMemoize,
} from 'app-components/utils/core/reselect';
import { registerCachedSelectors, registerCacheReset } from 'app-components/utils/core/reselect/selectorCacheRegistry';
import { intervalContainsInclusive } from 'app-components/utils/date';
import store from 'app-components/utils/redux-store';
import type RootState from 'app-components/utils/redux-store/rootState';

import { getAccountsById } from '../accounts/accountsSelectors';
import { isQBPAccount } from '../accounts/accountsUtils';
import type { Category } from '../categories/categoriesTypes';
import { chartOfAccountsUtils } from '../chart-of-accounts';
import { getCurrentDateString } from '../date/dateSelectors';
import type { DATE_REDUCER_KEY } from '../date/dateTypes';
import {
  buildLinkedInvoiceIndex,
  hasRealSplitItems,
  isInvoicePaymentSplitItem,
  isLinkedBillableSplitItem,
  type LinkedInvoiceIndex,
} from './splitsHelpers';
import { isSplitTxn } from './transactionsPredicates';
import {
  type Allocation,
  type AllocationProps,
  mkSplit,
  mkSplitItem,
  mkTransactionItem,
  type SplitItemProps,
  StatusChipTypes,
  TRANSACTIONS_REDUCER_KEY,
  type TransactionItem,
  type TransactionItemCombinedProps,
  type TransactionProps,
  type TransactionRecord,
  TransactionSubType,
} from './transactionsTypes';
// biome-ignore lint/suspicious/noImportCycles: SIMPL-29722
import * as transactionsUtils from './transactionsUtils';
// biome-ignore lint/suspicious/noImportCycles: SIMPL-29722
import { getParsedExpressions, transactionMatchesExpression } from './transactionsUtils';

const log = getLogger('flux/transactions/transactionsSelectors.js');

type State = Pick<
  RootState,
  | typeof TRANSACTIONS_REDUCER_KEY
  | typeof ACCOUNTS_REDUCER_KEY
  | typeof SECURITIES_REDUCER_KEY
  | typeof CATEGORIES_REDUCER_KEY
  | typeof FEATURE_FLAGS_REDUCER_KEY
  | typeof DATE_REDUCER_KEY
>;

type SimpleState = Omit<State, typeof CATEGORIES_REDUCER_KEY | typeof SECURITIES_REDUCER_KEY>;

export const getTransactionsStore = (state: SimpleState) => state[TRANSACTIONS_REDUCER_KEY];
export const getLoadPending = (state: SimpleState) => getTransactionsStore(state).loadPending;
export const getError = (state: SimpleState) => getTransactionsStore(state).error;
export const getIsLoading = (state: SimpleState) => getTransactionsStore(state).isLoading;
export const getLastSyncDate = (state: SimpleState) => getTransactionsStore(state).lastSyncDate;
export const getResourcesById = (state: SimpleState) => getTransactionsStore(state).resourcesById;

const filterToOrderedMap = (
  transactions: ImMap<string, TransactionRecord>,
  test: (transaction: TransactionRecord) => unknown,
): OrderedMap<string, TransactionRecord> => transactions.toSeq().filter(test).toOrderedMap();

type CandidateSource = (state: SimpleState) => ImMap<string, TransactionRecord>;

/** Retain matching raw records in traversal order, reusing equal subsets after unrelated edits. */
export const createTransactionCandidateSelector = (
  predicate: (transaction: TransactionRecord, today: string) => boolean,
  // Only date-sensitive predicates subscribe to the store date.
  getDate: (state: SimpleState) => string = () => '',
  // A candidate set that contains every match, so the predicate scans fewer transactions.
  getSource: CandidateSource = getResourcesById,
): OutputSelector<[CandidateSource, typeof getDate], OrderedMap<string, TransactionRecord>> => {
  // Store edits re-test only the transactions they changed; a smaller source is rescanned when it changes.
  const filterCandidates = selectorsHelpers.createIncrementalFilter(filterToOrderedMap);
  return createSelector(
    [getSource, getDate],
    (transactions, today): OrderedMap<string, TransactionRecord> =>
      filterCandidates(transactions, [today], (transaction) => predicate(transaction, today)),
    { memoizeOptions: { resultEqualityCheck: immutableEntriesShallowEqual<string, TransactionRecord> } },
  );
};

/**
 * Indexes downloaded transaction IDs that are matched to a sales receipt.
 *
 * The previous UI lookup walked every transaction once per mounted card and per Redux update.
 * Memoizing the index against the transaction collection makes those card lookups constant-time
 * and shares the single rebuild across every consumer.
 */
export const getSalesReceiptMatchedTransactionIds = createSelector(
  getResourcesById,
  (transactionsById): ReadonlySet<string> => {
    const matchedTransactionIds = new Set<string>();
    transactionsById.forEach((transaction) => {
      const matchedTransactionId = transaction.matchedTxn?.id;
      if (
        matchedTransactionId &&
        transaction.type === 'INVOICE' &&
        transaction.subtype === TransactionSubType.SalesReceipt
      ) {
        matchedTransactionIds.add(matchedTransactionId);
      }
    });
    return matchedTransactionIds;
  },
);

export const getIsTxnMatchedToSalesReceipt = (state: SimpleState, transactionId: string | null | undefined): boolean =>
  Boolean(transactionId && getSalesReceiptMatchedTransactionIds(state).has(transactionId));

/** Memoized so linked-billable rows in one render share a single O(N) scan. */
export const getLinkedInvoiceIndex = createSelector(getResourcesById, buildLinkedInvoiceIndex);

/** Null until the initial transaction load completes; background refreshes keep using loaded data. */
export const getLinkedInvoiceIndexIfLoaded = createSelector(
  getLoadPending,
  getLinkedInvoiceIndex,
  (loadPending, index): LinkedInvoiceIndex | null => (loadPending ? null : index),
);

export const getDateOnAfter = (state: SimpleState) => getTransactionsStore(state).dateOnAfter;

/** Default history start: the rolling cutoff, else the since preference. Unmemoized so it advances at midnight. */
export const getDefaultDateOnAfter = (
  state: Pick<RootState, typeof FEATURE_FLAGS_REDUCER_KEY | typeof PREFERENCES_V2_REDUCER_KEY>,
): string | undefined => {
  const daysToLoad = featureFlagsSelectors.getFeatureFlag(state, 'transactionsDaysToLoadRelease');
  if (daysToLoad && typeof daysToLoad === 'number') {
    return DateTime.now().minus({ days: daysToLoad }).toISODate() ?? undefined;
  }
  const preference: unknown = preferencesV2Selectors.getPreferenceValue(state, 'dateOnAfter');
  return typeof preference === 'string' && preference ? preference : undefined;
};

/** True when the loaded history is not the default window and no load is in flight. */
export const getShouldResetHistoryWindow = (
  state: SimpleState & Pick<RootState, typeof PREFERENCES_V2_REDUCER_KEY>,
): boolean => !getIsLoading(state) && getDateOnAfter(state) !== getDefaultDateOnAfter(state);

export const getEarliestDateOn = (state: SimpleState) => getTransactionsStore(state).earliestDateOn;

// NOTE: this is to break circular dependencies
const getSecuritiesResourcesById = (state: State) => state[SECURITIES_REDUCER_KEY].resourcesById;

export const existsTransactionWithTag = createSelector(
  getResourcesById,
  (_: unknown, { id }: { id: string }) => id,
  (state, tagId) => Boolean(state.find((txn) => Boolean(transactionsUtils.txnHasTag(txn, tagId)))),
);

type TransactionsByAccountId = ImMap<string, ImMap<string, TransactionRecord>>;

// the typedefs for groupBy return a general keyed sequence, but we need an immutable map that has `withMutations`, and we know it does have this method since this selector works in production
const groupByAccountId = (transactions: ImMap<string, TransactionRecord>) =>
  transactions.groupBy((transaction) => transaction.accountId) as unknown as TransactionsByAccountId;

let lastGrouping: { version: number; size: number; groups: TransactionsByAccountId } | undefined;
registerCacheReset(() => {
  lastGrouping = undefined;
});

// With the same keys in the map, groupBy would build every group from the same entries in the same
// order, so replacing changed records in place gives the same groups. Maps of up to 8 entries are
// reordered when they grow, so they are always regrouped.
function regroupChanges(
  transactions: ImMap<string, TransactionRecord>,
  previous: NonNullable<typeof lastGrouping>,
): TransactionsByAccountId | undefined {
  if (previous.size <= 8 || transactions.size <= 8) return undefined;
  const changes = resourceChangeLog.getResourceChangesSince(transactions, previous.version);
  if (!changes || changes.removed) return undefined;
  let groups = previous.groups;
  for (const id of changes.keys) {
    const transaction = transactions.get(id);
    const accountId = transaction?.accountId as string;
    const group = groups.get(accountId);
    // New transactions and account moves change which group holds a key.
    if (!transaction || !group?.has(id)) return undefined;
    groups = groups.set(accountId, group.set(id, transaction));
  }
  return groups;
}

/** Edits replace only the edited transactions' groups; the other groups keep their identity. */
export const getTransactionsByAccountIdRaw = createSelector(getResourcesById, (transactions) => {
  const groups = (lastGrouping && regroupChanges(transactions, lastGrouping)) || groupByAccountId(transactions);
  lastGrouping = { version: resourceChangeLog.getResourceVersion(transactions), size: transactions.size, groups };
  return groups;
});

export const TxnTypes = Object.freeze({
  GOALS: 1,
  REGULAR_TXNS: 2,
  UNACCEPTED_SCHEDULED_TXNS: 4,
  REFUND_VIRTUAL_TXN: 8,
  INVESTMENT_TXN: 16,
  A2A_TRANSFER: 32,
  A2A_SHADOW: 64,
  ESTIMATES: 128,

  ALL: 0xffff,
});
export const txnTypesCash = TxnTypes.REGULAR_TXNS | TxnTypes.UNACCEPTED_SCHEDULED_TXNS;
export const txnTypesDefault = txnTypesCash | TxnTypes.INVESTMENT_TXN;
export const txnTypesCashAndRefund = txnTypesCash | TxnTypes.REFUND_VIRTUAL_TXN;
export const txnTypesCashAndPlanned = txnTypesCashAndRefund | TxnTypes.A2A_TRANSFER;

/** The single TxnTypes bit a transaction belongs to. */
const classifyTransaction = (txn: TransactionProps, goalAccountIds: ImSet<string>): number => {
  if (transactionsUtils.isRefundIncompleteTxn(txn)) {
    return TxnTypes.REFUND_VIRTUAL_TXN;
  }
  if (transactionsUtils.isGoalTxnInNonGoalAccount(txn, goalAccountIds) || txn.subtype === 'GOAL_STARTING_AMOUNT') {
    return TxnTypes.GOALS;
  }
  if (transactionsUtils.isUnacceptedScheduledTxn(txn)) {
    return TxnTypes.UNACCEPTED_SCHEDULED_TXNS;
  }
  if (txn.type === 'INVESTMENT') {
    return TxnTypes.INVESTMENT_TXN;
  }
  if (transactionsUtils.isPlannedA2ATransfer(txn)) {
    return TxnTypes.A2A_TRANSFER;
  }
  if (transactionsUtils.isCancelledA2aTransfer(txn)) {
    return TxnTypes.A2A_SHADOW;
  }
  if (transactionsUtils.isEstimateTransaction(txn)) {
    return TxnTypes.ESTIMATES;
  }
  return TxnTypes.REGULAR_TXNS;
};

// Records are immutable and the class depends only on the record and the goal-account set, so each
// pair is classified once. Plain objects can change, so they are classified on every call.
const transactionClassCache = new WeakMap<ImSet<string>, WeakMap<TransactionProps, number>>();

const getTransactionClass = (txn: TransactionProps, goalAccountIds: ImSet<string>): number => {
  if (!ImRecord.isRecord(txn) || typeof goalAccountIds !== 'object' || goalAccountIds === null) {
    return classifyTransaction(txn, goalAccountIds);
  }
  let classByTxn = transactionClassCache.get(goalAccountIds);
  if (!classByTxn) {
    classByTxn = new WeakMap();
    transactionClassCache.set(goalAccountIds, classByTxn);
  }
  let txnClass = classByTxn.get(txn);
  if (txnClass === undefined) {
    txnClass = classifyTransaction(txn, goalAccountIds);
    classByTxn.set(txn, txnClass);
  }
  return txnClass;
};

/** The class does not depend on the date; `_todayISO` is accepted for existing callers. */
export const transactionOfTypes = (
  txn: TransactionProps,
  goalAccountIds: ImSet<string>,
  txnTypes: number = txnTypesDefault,
  _todayISO?: string,
): number => txnTypes & getTransactionClass(txn, goalAccountIds);

/** Boolean eligibility for predicate chains; transactionOfTypes retains its bitmask result. */
export const isTransactionOfTypes = (
  txn: TransactionProps,
  goalAccountIds: ImSet<string>,
  txnTypes: number = txnTypesDefault,
  todayISO?: string,
): boolean => Boolean(transactionOfTypes(txn, goalAccountIds, txnTypes, todayISO));

export const getTransactionsOfTypes = createCachedSelector(
  getResourcesById,
  accountsSelectors.getGoalAccountIds,
  (_state: unknown, txnTypes: number | undefined) => txnTypes || txnTypesDefault,
  getCurrentDateString,
  (transactionsById, goalAccountIds, txnTypes, todayISO) =>
    transactionsById.filter((transaction) => transactionOfTypes(transaction, goalAccountIds, txnTypes, todayISO)),
)({
  keySelector: (_state, txnTypes) => txnTypes || 'default',
});

export const getTransactionsById = (state: State) => getTransactionsOfTypes(state, txnTypesDefault);

// getTransactionsForAccountIdSelectors
//
// This selector returns a map of selectors that can then be used to get transactions
// filtered by txn-types for a specific account.
//
// We are using this level of indirection so that the returned transactions for each
// account can retain its filtered result when transactions in other accounts change.
// Account membership, goal classification, date or transaction types recreate the family.
//
// The selectors returned by this method are not typical selectors. Instead of
// a state and props, the expected arguments are a map of transactions by
// account ID.
//
export const getTransactionsForAccountIdSelectors = createCachedSelector(
  accountsSelectors.getAllAccountIds,
  accountsSelectors.getGoalAccountIds,
  (_state: unknown, props: { txnTypes?: number } | null | undefined) => (props && props.txnTypes) || txnTypesDefault,
  getCurrentDateString,
  (allAccountIds, goalAccountIds, txnTypes, todayISO) =>
    ImMap<
      string,
      ((accountsById: ImMap<string, ImMap<string, TransactionRecord>>) => ImMap<string, TransactionRecord>) & {
        recomputations: () => number;
      }
    >().withMutations((map) => {
      allAccountIds.forEach((accountId) => {
        const selector = createSelector(
          (transactionsByAccountId: ImMap<string, ImMap<string, TransactionRecord>>) =>
            transactionsByAccountId.get(accountId),
          (txnsById) => {
            // handle special case of no transactions in account
            if (txnsById === undefined || txnsById.size === 0) {
              return ImMap<string, TransactionRecord>();
            }
            const filteredTxnsById = txnsById.filter((txn) =>
              transactionOfTypes(txn, goalAccountIds, txnTypes, todayISO),
            );
            return filteredTxnsById;
          },
          {
            memoize: lruMemoize,
            memoizeOptions: {
              maxSize: 1,
              equalityCheck: immutableEntriesShallowEqual,
              resultEqualityCheck: immutableEntriesShallowEqual,
            },
          },
        );
        map.set(accountId, selector);
      });
    }),
)({
  keySelector: (_state: unknown, props: { txnTypes?: number } | null | undefined) =>
    (props && props.txnTypes) || txnTypesDefault,
  cacheObject: new FlatMapCache(),
});

export const getTransactionsByAccountId = createSelector(
  [
    accountsSelectors.getAllAccountIds,
    getTransactionsByAccountIdRaw,
    (state: SimpleState, props?: { txnTypes?: number }) => getTransactionsForAccountIdSelectors(state, props),
  ],
  (allAccountIds, txnsByAccountId, txnsForAccountIdSelectors) =>
    ImMap<string, ImMap<string, TransactionRecord>>().withMutations((map) => {
      allAccountIds.forEach((accountId) => {
        const txnsForAccountIdSelector = txnsForAccountIdSelectors.get(accountId);
        if (txnsForAccountIdSelector) {
          map.set(accountId, txnsForAccountIdSelector(txnsByAccountId));
        }
      });
    }),
  {
    // Reuse equal outer maps even when consumers interleave different transaction-type scopes.
    memoize: resettableLruMemoize,
    memoizeOptions: {
      maxSize: 10,
      resultEqualityCheck: immutableEntriesShallowEqual,
    },
  },
);

export interface CreditCardPaymentGroup {
  payingAccountId: string;
  transactionIds: string[];
}

// Some payments (e.g. QCS-linked transfers) don't carry a real paying account - only a
// balance-adjustment sentinel category (CREDIT_CARD_PAYMENT/TRANSFER). Those land here instead.
export const UNKNOWN_PAYING_ACCOUNT = 'Other';

// Not-yet-posted scheduled instances carry their date in `stDueOn`; posted transactions use `postedOn`.
const getEffectiveTxnDate = (txn: TransactionProps): string | null | undefined => txn.postedOn || txn.stDueOn;

const isWithinDateRange = (txn: TransactionProps, start: string, end: string): boolean => {
  const txnDate = getEffectiveTxnDate(txn);
  return Boolean(txnDate) && txnDate! >= start && txnDate! <= end;
};

// Positive amount means money flowed into this account; combined with the transfer/credit-card-payment
// COA check, this excludes refunds and ordinary balance adjustments from the payment list.
const isPaymentIntoAccount = (txn: TransactionProps): boolean => {
  if (txn.amount <= 0) return false;

  if (isSplitTxn(txn)) {
    return Boolean(txn.split?.items?.find((item) => chartOfAccountsUtils.isTransferOrCreditCardPaymentCoa(item.coa)));
  }

  return Boolean(
    txn.coa &&
      ((txn.coa.type === chartOfAccountsTypes.CoaTypeEnum.ACCOUNT && txn.coa.id !== txn.accountId) ||
        (txn.coa.type === chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT &&
          chartOfAccountsUtils.isTransferOrCreditCardPaymentCoa(txn.coa))),
  );
};

const getPayingAccountId = (txn: TransactionProps): string =>
  txn.coa?.type === chartOfAccountsTypes.CoaTypeEnum.ACCOUNT ? txn.coa.id : UNKNOWN_PAYING_ACCOUNT;

// Groups transfer/credit-card-payment transaction ids by the paying (source) account within
// [start, end] (inclusive), sorted ascending by effective date. Covers scheduled, overdue, and posted
// payments alike, since `getTransactionsByAccountId` already includes not-yet-posted scheduled instances.
// Paid payments are excluded unless `showPaid` is true.
export const getCreditCardPaymentsByAccount = createCachedSelector(
  [
    accountsSelectors.getPersonalCreditCardAccounts,
    (state: SimpleState) => getTransactionsByAccountId(state, { txnTypes: txnTypesDefault }),
    (_state: SimpleState, start: string) => start,
    (_state: SimpleState, _start: string, end: string) => end,
    (_state: SimpleState, _start: string, _end: string, showPaid: boolean) => showPaid,
    dateSelectors.getCurrentDateString,
  ],
  (creditCardAccountsById, transactionsByAccountId, start, end, showPaid, currentDay): CreditCardPaymentGroup[] => {
    // Keyed by paying account; values are [txnId, effective date] so the later sort doesn't need
    // to hold onto (or re-derive the date from) the full transaction record.
    const entriesByPayingAccountId = new Map<string, [string, string][]>();

    creditCardAccountsById.forEach((account, cardAccountId) => {
      if (account.isClosed) {
        return;
      }
      const accountTxns = transactionsByAccountId.get(cardAccountId);
      if (!accountTxns) {
        return;
      }

      const matchedTxnIdsFromReminders = transactionsUtils.getMatchedTxnIdsFromReminders(accountTxns.values());

      accountTxns.forEach((txn, id) => {
        // Is the txn an un-accepted placeholder reminder?
        // If so (A reminder), decide whether it is paid by checking whether it has been matched.
        // If not (A transaction), decide whether it is paid by simply checking if it is upcoming!
        const txnIsPaid = transactionsUtils.isUnacceptedScheduledTxn(txn)
          ? transactionsUtils.isRealizedReminder(txn)
          : !transactionsUtils.isUpcoming(txn, currentDay);

        if (
          matchedTxnIdsFromReminders.has(id) ||
          !isWithinDateRange(txn, start, end) ||
          !isPaymentIntoAccount(txn) ||
          (!showPaid && txnIsPaid)
        ) {
          return;
        }
        const payingAccountId = getPayingAccountId(txn);
        const entries = entriesByPayingAccountId.get(payingAccountId) ?? [];
        entries.push([id, getEffectiveTxnDate(txn) || '']);
        entriesByPayingAccountId.set(payingAccountId, entries);
      });
    });

    return Array.from(entriesByPayingAccountId.entries()).map(([payingAccountId, entries]) => ({
      payingAccountId,
      transactionIds: entries
        .sort(([, aDate], [, bDate]) => (aDate < bDate ? -1 : aDate > bDate ? 1 : 0))
        .map(([id]) => id),
    }));
  },
)({
  keySelector: (_state: SimpleState, start: string, end: string, showPaid: boolean) => `${start}|${end}|${showPaid}`,
  cacheObject: new LruObjectCache({ cacheSize: 2 }),
});

export const getCreditCardPaymentsTotal = createCachedSelector(
  [
    (state: SimpleState, start: string, end: string, showPaid: boolean) =>
      getCreditCardPaymentsByAccount(state, start, end, showPaid),
    getResourcesById,
  ],
  (groups, transactionsById): number =>
    groups.reduce(
      (sum, group) =>
        sum + group.transactionIds.reduce((groupSum, id) => groupSum + (transactionsById.get(id)?.amount ?? 0), 0),
      0,
    ),
)({
  keySelector: (_state: SimpleState, start: string, end: string, showPaid: boolean) => `${start}|${end}|${showPaid}`,
  cacheObject: new LruObjectCache({ cacheSize: 2 }),
});

const sortStDueOn = (a: TransactionProps, b: TransactionProps) =>
  (a.stDueOn || '') < (b.stDueOn || '') ? -1 : (a.stDueOn || '') > (b.stDueOn || '') ? 1 : 0;

/** Raw reminders in resource traversal order; consumers retain their own type/date/account rules. */
// Resolve utility predicates when used: transactionsUtils imports this module through shared domains.
export const getPendingTransactionCandidates = createTransactionCandidateSelector((transaction) =>
  transactionsUtils.isUnacceptedScheduledTxn(transaction),
);

// Preserve the input collection type when reconstructing filtered groups (including OrderedMap fixtures).
const getTransactionCollectionTemplate = createSelector(
  [getResourcesById],
  (transactions): ImMap<string, TransactionRecord> => transactions.clear(),
);

const getEligiblePendingTransactionCandidates = createSelector(
  [getPendingTransactionCandidates, accountsSelectors.getGoalAccountIds, getCurrentDateString],
  (transactions, goalAccountIds, today): OrderedMap<string, TransactionRecord> =>
    transactions.filter((transaction) =>
      Boolean(transactionOfTypes(transaction, goalAccountIds, txnTypesDefault, today)),
    ),
  { memoizeOptions: { resultEqualityCheck: immutableEntriesShallowEqual<string, TransactionRecord> } },
);

const SeriesAccount = ImRecord({ id: '', accountId: '' });
const getUpcomingSeriesAccounts = createSelector(
  [(_state: SimpleState, series: ImMap<string, RecordOf<ScheduledTransactionType>>): typeof series => series],
  (series): ImList<RecordOf<{ id: string; accountId: string }>> =>
    series
      .valueSeq()
      .flatMap((model) =>
        model.id && model.transaction?.accountId
          ? [SeriesAccount({ id: model.id, accountId: model.transaction.accountId })]
          : [],
      )
      .toList(),
  { memoizeOptions: { resultEqualityCheck: is } },
);

/** First reminder's display date per requested series; callers retain the template dueOn fallback. */
export const getUpcomingTransactionDatesByModel = createSelector(
  [
    getEligiblePendingTransactionCandidates,
    getUpcomingSeriesAccounts,
    accountsSelectors.getAllAccountIds,
    getTransactionCollectionTemplate,
  ],
  (transactions, series, accountIds, template): ImMap<string, string> => {
    const groupsByAccount = new Map<string, Map<string, ImMap<string, TransactionRecord>>>();
    series.forEach(({ id, accountId }) => {
      if (!accountIds.includes(accountId)) return;
      let groups = groupsByAccount.get(accountId);
      if (!groups) {
        groups = new Map();
        groupsByAccount.set(accountId, groups);
      }
      if (!groups.has(id)) groups.set(id, template.asMutable());
    });
    transactions.forEach((transaction, id) => {
      const groups = transaction.accountId ? groupsByAccount.get(transaction.accountId) : undefined;
      if (transaction.stModelId) groups?.get(transaction.stModelId)?.set(id, transaction);
    });
    const datesByAccount = new Map<string, Map<string, string>>();
    groupsByAccount.forEach((groups, accountId) => {
      const dates = new Map<string, string>();
      groups.forEach((group, id) => {
        // Scan the original group traversal order so a due-date tie retains the first row.
        const first = group.reduce<TransactionRecord | undefined>(
          (earliest, transaction) => (!earliest || sortStDueOn(transaction, earliest) < 0 ? transaction : earliest),
          undefined,
        );
        if (first) dates.set(id, first.postedOn || first.stDueOn || '');
      });
      datesByAccount.set(accountId, dates);
    });
    return ImMap<string, string>().withMutations((dates) => {
      // Preserve duplicate IDs' last nonempty requested-account behavior.
      series.forEach(({ id, accountId }) => {
        const date = datesByAccount.get(accountId)?.get(id);
        if (date !== undefined) dates.set(id, date);
      });
    });
  },
  { memoizeOptions: { resultEqualityCheck: is } },
);

// A series' upcoming group depends only on its account's reminders, so callers with different series
// maps can share it. Unchanged groups keep their identity across transaction edits.
let previousUpcomingGroups = new Map<string, ImMap<string, TransactionRecord>>();
registerCacheReset(() => {
  previousUpcomingGroups = new Map();
});

// Groups come from the eligible reminders rather than every account's transactions, so edits that
// leave the reminders unchanged reuse the result.
export const getAllUpcomingTransactions = createSelector(
  getEligiblePendingTransactionCandidates,
  accountsSelectors.getAllAccountIds,
  getTransactionCollectionTemplate,
  (_state: unknown, schTxns: ImMap<string, RecordOf<ScheduledTransactionType>>) => schTxns,
  (transactions, accountIds, template, schTxns): ImMap<string, ImMap<string, TransactionRecord>> => {
    if (accountIds.isEmpty() || schTxns.isEmpty()) {
      return ImMap<string, ImMap<string, TransactionRecord>>();
    }
    const upcomingByAccount = new Map<string, Map<string, ImMap<string, TransactionRecord>>>();
    schTxns.forEach(({ id, transaction }) => {
      const accountId = transaction?.accountId;
      if (!id || !accountId || !accountIds.includes(accountId)) return;
      let groups = upcomingByAccount.get(accountId);
      if (!groups) {
        groups = new Map();
        upcomingByAccount.set(accountId, groups);
      }
      // Retain the transactions' collection type so equal-date sorting keeps the existing order.
      if (!groups.has(id)) groups.set(id, template.asMutable());
    });

    // Reminders in transaction order, as the account buckets held them.
    transactions.forEach((txn, txnId) => {
      const groups = txn.accountId ? upcomingByAccount.get(txn.accountId) : undefined;
      const group = txn.stModelId ? groups?.get(txn.stModelId) : undefined;
      group?.set(txnId, txn);
    });
    upcomingByAccount.forEach((groups) => {
      groups.forEach((group, id) => {
        const sorted = group.asImmutable().sort(sortStDueOn);
        const previous = previousUpcomingGroups.get(id);
        groups.set(id, previous && immutableEntriesShallowEqual(previous, sorted) ? previous : sorted);
      });
    });

    const nextUpcomingGroups = new Map<string, ImMap<string, TransactionRecord>>();
    const result = ImMap<string, ImMap<string, TransactionRecord>>().withMutations((mutableMap) => {
      schTxns.forEach(({ id, transaction }) => {
        const group = id && transaction?.accountId ? upcomingByAccount.get(transaction.accountId)?.get(id) : undefined;
        if (id && group) nextUpcomingGroups.set(id, group);
        if (id && group && !group.isEmpty()) mutableMap.set(id, group);
      });
    });
    previousUpcomingGroups = nextUpcomingGroups;
    return result;
  },
);

const getRefundTransactionCandidates = createTransactionCandidateSelector((transaction) =>
  transactionsUtils.isRefundTxn(transaction),
);

const getEligibleRefundTransactions = createSelector(
  [
    getRefundTransactionCandidates,
    accountsSelectors.getGoalAccountIds,
    getCurrentDateString,
    getTransactionCollectionTemplate,
  ],
  (transactions, goalAccountIds, today, template): ImMap<string, TransactionRecord> =>
    template.withMutations((result) => {
      transactions.forEach((transaction, id) => {
        if (transactionOfTypes(transaction, goalAccountIds, txnTypesDefault | TxnTypes.REFUND_VIRTUAL_TXN, today)) {
          result.set(id, transaction);
        }
      });
    }),
  { memoizeOptions: { resultEqualityCheck: immutableEntriesShallowEqual<string, TransactionRecord> } },
);

export const getRefundActiveTransactions = createSelector(
  getEligibleRefundTransactions,
  getCurrentDateString,
  (transactionsById: ImMap<string, TransactionRecord>, todayISO) =>
    transactionsById
      .filter((transaction) => {
        const refundState = transactionsUtils.getRefundState(transaction, todayISO);
        return (
          refundState === transactionsUtils.RefundState.EXPECTED ||
          refundState === transactionsUtils.RefundState.OVERDUE
        );
      })
      .sortBy((transaction) => transaction.postedOn),
);

export const getRefundActiveTransactionsByAccountId = createCachedSelector(
  getRefundActiveTransactions,
  (_state: unknown, accountId: string) => accountId,
  (refundActiveTransactions, accountId) =>
    refundActiveTransactions.filter((transaction) => transaction.accountId === accountId),
)({
  keySelector: (_state: unknown, accountId) => accountId || 'none',
  cacheObject: new LruObjectCache({ cacheSize: 10 }),
});

export const getRefundActiveTransactionsTotal = createSelector(getRefundActiveTransactions, (transactionsById) =>
  transactionsById.reduce((total, transaction) => total + Number(transaction.amount ?? 0), 0),
);

export const getRefundActiveRelatedTxns = createCachedSelector(
  getRefundActiveTransactions,
  (_state: unknown, relatedTnxId: string) => relatedTnxId,
  (refundActiveTransactions, relatedTnxId) =>
    refundActiveTransactions.filter((transaction) => transaction.subtypeRelatedTxn?.id === relatedTnxId),
)({
  keySelector: (_state: unknown, relatedTnxId?: string) => relatedTnxId ?? '',
  cacheObject: new LruObjectCache({ cacheSize: 10 }),
});

export const getRefundCompleteTransactions = createSelector(
  getEligibleRefundTransactions,
  getCurrentDateString,
  (transactionsById, todayISO) =>
    transactionsById
      .filter((transaction) => {
        const refundState = transactionsUtils.getRefundState(transaction, todayISO);
        return (
          refundState === transactionsUtils.RefundState.COMPLETE || refundState === transactionsUtils.RefundState.SHADOW
        );
      })
      .sort((transaction1, transaction2) => transaction2.postedOn?.localeCompare(transaction1.postedOn) ?? 0),
);

export const getRefundCompleteTransactionsTotal = createSelector(getRefundCompleteTransactions, (transactionsById) =>
  transactionsById.reduce((total, transaction) => total + Number(transaction.amount ?? 0), 0),
);

export const getRefundCompleteRelatedTxns = createCachedSelector(
  getRefundCompleteTransactions,
  (_state: unknown, relatedTnxId: string) => relatedTnxId,
  (refundCompleteTransactions, relatedTnxId) =>
    refundCompleteTransactions.filter((transaction) => transaction.subtypeRelatedTxn?.id === relatedTnxId),
)({
  keySelector: (_state: unknown, relatedTnxId?: string) => relatedTnxId ?? '',
  cacheObject: new LruObjectCache({ cacheSize: 10 }),
});

export const getRefundLinkEligibleTransactions = createCachedSelector(
  (state: State, transaction: TransactionProps) =>
    getTransactionsByAccountId(state || store?.getState()).get(transaction.accountId || ''),
  (transactions) =>
    transactions
      ?.filter((transaction) => (transaction.amount || 0) > 0)
      .filter((transaction) => transaction.matchState === 'NOT_MATCHED')
      .filter(transactionsUtils.isBankDownloadedTxn)
      .sortBy((transaction) => transaction.postedOn)
      .reverse(),
)({
  keySelector: (_state: unknown, transaction?: TransactionProps) => transaction?.accountId ?? '',
  cacheObject: new LruObjectCache({ cacheSize: 12 }),
});

export const getTransactionsForAccountIds = createCachedSelector(
  (_state: unknown, props: { accountIds?: string[] | ImList<string> | null }) => props.accountIds,
  getTransactionsByAccountId,
  accountsSelectors.getAccountsById,
  (accountIds, transactionsByAccountId, accountsById) => {
    let accountIdsList = ImList(accountIds || []);
    if (!accountIds || accountIdsList.size === 0) {
      accountIdsList = accountsById.map(getId).toList();
    }

    let returnTransactions = ImMap<string, TransactionRecord>();
    accountIdsList.forEach((id) => {
      const txns = transactionsByAccountId.get(id);
      if (txns) {
        returnTransactions = returnTransactions.merge(txns);
      }
    });

    return returnTransactions;
  },
)({
  keySelector: (_state: unknown, props: { accountIds?: string[] | ImList<string> | null }) =>
    props.accountIds ? ImList(props.accountIds).hashCode() : 'all',
  cacheObject: new FlatMapCache(),
});

const isPostedAndAcceptedTransaction = (txn: TransactionRecord, today: string): boolean =>
  !transactionsUtils.isUnacceptedScheduledTxn(txn) && (txn.postedOn ?? 0) <= today;

/**
 * Transactions that should render as posted register rows for the supplied accounts.
 * Keeps account scoping from `getTransactionsForAccountIds`, while excluding upcoming
 * and unaccepted reminder instances without applying broader global register filters.
 */
export const getPostedTransactionsForAccountIds = createCachedSelector(
  getTransactionsForAccountIds,
  dateSelectors.getCurrentDateString,
  (transactions, today) => transactions.filter((txn) => isPostedAndAcceptedTransaction(txn, today)),
)({
  keySelector: (_state: unknown, props: { accountIds?: string[] | ImList<string> | null }) =>
    props.accountIds ? ImList(props.accountIds).hashCode() : 'all',
  cacheObject: new FlatMapCache(),
});

type PageAccountScope = 'hidden' | 'shown' | 'perTransaction';

// used for excluding upcoming transactions + investment transactions + connected loan txns for txnPage on mobile
// One pass into a list: maps by account and by id were most of the cost of opening a register.
export const getTransactionsPageTransactions: (state: SimpleState) => ReadonlyArray<TransactionRecord> = createSelector(
  getResourcesById,
  accountsSelectors.getAccountsById,
  accountsSelectors.getGoalAccountIds,
  dateSelectors.getCurrentDateString,
  (state: SimpleState): string => featureFlagsSelectors.getFeatureFlag(state, 'exposeInvestmentTransactions') || 'HIDE',
  (state: SimpleState): boolean => featureFlagsSelectors.getFeatureFlags(state).get('hideConnectedLoanTransactions'),
  (transactions, accounts, goalAccountIds, today, exposeInvestmentTransactions, hideConnectedLoanTransactions) => {
    // Nearly every transaction in an account gets the same answer, so each account is looked up once.
    const scopes = new Map<string | null | undefined, PageAccountScope>();
    const scopeOf = (txn: TransactionRecord): PageAccountScope => {
      const account = accounts.get(txn.accountId ?? '');
      if (!account || (hideConnectedLoanTransactions && transactionsUtils.isConnectedLoanTransaction(txn, accounts))) {
        return 'hidden';
      }
      return account.type === 'INVESTMENT' ? 'perTransaction' : 'shown';
    };

    const list: TransactionRecord[] = [];
    transactions.forEach((txn) => {
      let scope = scopes.get(txn.accountId);
      if (!scope) {
        scope = scopeOf(txn);
        scopes.set(txn.accountId, scope);
      }
      if (
        scope !== 'hidden' &&
        (scope === 'shown' || transactionsUtils.isExposedTransaction(txn, accounts, exposeInvestmentTransactions)) &&
        isTransactionOfTypes(txn, goalAccountIds) &&
        isPostedAndAcceptedTransaction(txn, today)
      ) {
        list.push(txn);
      }
    });
    return list;
  },
);

export const getTransactionById = (state: State, id: string) => getResourcesById(state).get(id);

type ExclusionDisplayField = 'isExcludedFromF2S' | 'isExcludedFromReports';
type ExclusionDisplayState = {
  value: boolean | null;
  indeterminate: boolean;
};
type ManagedExclusionDisplayState = ExclusionDisplayState & {
  managedPerSplit: boolean;
};
type SplitExclusionDisplayState = {
  isExcludedFromF2S: boolean;
  isExcludedFromReports: boolean;
};
export type SplitManagedVisibilityState = {
  hasAnyManagedVisibility: boolean;
  spendingDisplayState: ManagedExclusionDisplayState;
  reportsDisplayState: ManagedExclusionDisplayState;
};

export const getTransferLinkedTransaction = (
  transactionsById: ReturnType<typeof getResourcesById> | null | undefined,
  transfer: SplitItemProps['transfer'] | null | undefined,
): TransactionRecord | null => {
  if (!transactionsById || !transfer) {
    return null;
  }

  if (transfer.id && transactionsById.has(transfer.id)) {
    return transactionsById.get(transfer.id) ?? null;
  }

  if (!transfer.clientId) {
    return null;
  }

  let found: TransactionRecord | null = null;
  transactionsById.forEach((candidate) => {
    if (found || candidate?.clientId !== transfer.clientId) {
      return;
    }

    found = candidate;
  });

  return found;
};

const getManagedExcludedDisplayState = (
  txn: Pick<TransactionProps, 'split' | 'isExcludedFromF2S' | 'isExcludedFromReports'> | null | undefined,
  transactionsById: ReturnType<typeof getResourcesById> | null | undefined,
  field: ExclusionDisplayField,
): ManagedExclusionDisplayState => {
  const parentExcluded = Boolean(txn?.[field]);
  const splitItems = txn?.split?.items;
  if (!splitItems) {
    return { value: parentExcluded, indeterminate: false, managedPerSplit: false };
  }

  const effectiveFlags: boolean[] = [];
  let managedPerSplit = hasRealSplitItems(txn?.split?.items);
  splitItems.forEach((rawItem) => {
    const item = rawItem as SplitItemProps;
    const itemValue = item[field];
    if (itemValue != null) {
      managedPerSplit = true;
      effectiveFlags.push(Boolean(itemValue));
      return;
    }

    const otherSide = getTransferLinkedTransaction(transactionsById, item.transfer);
    if (otherSide) {
      managedPerSplit = true;
      effectiveFlags.push(Boolean(otherSide?.[field]));
      return;
    }

    effectiveFlags.push(parentExcluded);
  });

  if (effectiveFlags.length === 0) {
    return { value: parentExcluded, indeterminate: false, managedPerSplit };
  }

  const anyTrue = effectiveFlags.some(Boolean);
  const anyFalse = effectiveFlags.some((value) => !value);
  if (anyTrue && anyFalse) {
    return { value: null, indeterminate: true, managedPerSplit };
  }

  return { value: anyTrue, indeterminate: false, managedPerSplit };
};

export const getExcludedDisplayState = (
  txn: Pick<TransactionProps, 'split' | 'isExcludedFromF2S' | 'isExcludedFromReports'> | null | undefined,
  transactionsById: ReturnType<typeof getResourcesById> | null | undefined,
  field: ExclusionDisplayField,
): ExclusionDisplayState => {
  const { managedPerSplit: _managedPerSplit, ...displayState } = getManagedExcludedDisplayState(
    txn,
    transactionsById,
    field,
  );
  return displayState;
};

export const getSplitManagedVisibilityState = (
  txn: Pick<TransactionProps, 'split' | 'isExcludedFromF2S' | 'isExcludedFromReports'> | null | undefined,
  transactionsById: ReturnType<typeof getResourcesById> | null | undefined,
): SplitManagedVisibilityState => {
  const spendingDisplayState = getManagedExcludedDisplayState(txn, transactionsById, 'isExcludedFromF2S');
  const reportsDisplayState = getManagedExcludedDisplayState(txn, transactionsById, 'isExcludedFromReports');
  const hasAnyManagedVisibility = spendingDisplayState.managedPerSplit || reportsDisplayState.managedPerSplit;

  return {
    hasAnyManagedVisibility,
    spendingDisplayState,
    reportsDisplayState,
  };
};

export const getSplitExclusionDisplayState = (
  state: State,
  {
    split,
    parentIsExcludedFromF2S,
    parentIsExcludedFromReports,
    transactionId,
  }: {
    split: Partial<
      Pick<
        SplitItemProps,
        'id' | 'amount' | 'coa' | 'isExcludedFromF2S' | 'isExcludedFromReports' | 'transfer' | 'isOverflow'
      >
    >;
    parentIsExcludedFromF2S?: boolean | null;
    parentIsExcludedFromReports?: boolean | null;
    transactionId?: string | null;
  },
): SplitExclusionDisplayState => {
  const persistedParentTxn = transactionId ? getTransactionById(state, transactionId) : undefined;
  const transactionsById = getResourcesById(state);
  const effectiveTxn = {
    split: mkSplit({
      items: [mkSplitItem(split)],
    }),
    isExcludedFromF2S: parentIsExcludedFromF2S ?? persistedParentTxn?.isExcludedFromF2S,
    isExcludedFromReports: parentIsExcludedFromReports ?? persistedParentTxn?.isExcludedFromReports,
  };

  return {
    isExcludedFromF2S: Boolean(getExcludedDisplayState(effectiveTxn, transactionsById, 'isExcludedFromF2S').value),
    isExcludedFromReports: Boolean(
      getExcludedDisplayState(effectiveTxn, transactionsById, 'isExcludedFromReports').value,
    ),
  };
};

export type CategorySummaryProps = {
  count: number;
  amount: number;
  amountByMonth: ImMap<number, number> | null;
  coa: ChartOfAccountProps | null;
  acctId: string | null;
  monthlyAverage: string | null;
};
export const CategorySummary = ImRecord<CategorySummaryProps>({
  count: 0,
  amount: 0,
  amountByMonth: null,
  coa: null,
  acctId: null,
  monthlyAverage: null,
});

export type CategoryUsageType = 'PERSONAL' | 'BUSINESS';
export type PopularCategorySuggestion = Readonly<Pick<CategorySummaryProps, 'count' | 'coa'>>;
export type PopularCategorySuggestions = ImList<PopularCategorySuggestion>;
export type PopularCats = ImList<RecordOf<CategorySummaryProps>>;
/** Supplies category suggestions without subscribing to the live selector. */
export type PopularCatsLookup = (usageType: CategoryUsageType) => PopularCats;
export type ReviewPopularCatsByUsageType = Readonly<Record<CategoryUsageType, PopularCats>>;

/** Frequency-ranked category suggestions, without the amount history used by monthly summaries. */
export const getPopularCategorySuggestions = createCachedSelector(
  (state: State) => getTransactionsByAccountId(state),
  (_state: State, usageType: CategoryUsageType): CategoryUsageType => usageType,
  categoriesSelectors.getCategoriesById,
  (transactionsByAccount, usageType, categoriesById): PopularCategorySuggestions => {
    let suggestions = ImMap<string, PopularCategorySuggestion>();

    transactionsByAccount.forEach((transactions) => {
      transactions.forEach((transaction) => {
        const { coa } = transaction;
        if (coa?.type !== chartOfAccountsTypes.CoaTypeEnum.CATEGORY) return;
        if (categoriesById.get(coa.id)?.usageType !== usageType) return;

        suggestions = suggestions.set(coa.id, {
          count: (suggestions.get(coa.id)?.count ?? 0) + 1,
          coa,
        });
      });
    });

    // Keep the same Immutable.Map iteration order as the full summary for equal-frequency ties.
    return suggestions.toList().sort((a, b) => b.count - a.count);
  },
)({
  keySelector: (_state: State, usageType: CategoryUsageType): CategoryUsageType => usageType,
  cacheObject: new LruObjectCache({ cacheSize: 2 }),
});

export const getMostPopularCatsByAccountId = createCachedSelector(
  getTransactionsByAccountId,
  (_state: State, props: { dateRange?: Interval } | undefined) => props && props.dateRange,
  (_state: State, props: { sortByAmount?: boolean } | undefined) => props && props.sortByAmount,
  (_state: State, props: { usageType?: string } | undefined) => props && props.usageType,
  categoriesSelectors.getCategoriesById,
  (txns, dateRange, sortByAmount, usageType, categoriesById) => {
    let catCount = ImMap<string, RecordOf<CategorySummaryProps>>();

    txns.forEach((acctTxns, acctId) => {
      acctTxns.forEach((txn) => {
        if (txn.coa && txn.coa.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY) {
          // ignore splits, transfers, and uncategorized

          // check date if it is supplied
          if (!dateRange || intervalContainsInclusive(dateRange, DateTime.fromISO(txn.postedOn))) {
            if (usageType) {
              const categoryUsageType = categoriesById.get(txn.coa.id)?.usageType;
              if (categoryUsageType !== usageType) return;
            }
            const catRecord = catCount.get(txn.coa.id);
            let amount = Number(txn.amount);
            let count = 1;
            const { month } = DateTime.fromISO(txn.postedOn);
            let amountByMonth = ImMap<number, number>().set(month, amount);

            if (catRecord && catRecord.amountByMonth) {
              amount = catRecord.amount + Number(txn.amount ?? 0);
              count = catRecord.count + 1;
              amountByMonth = catRecord.amountByMonth.set(month, (catRecord.amountByMonth.get(month) || 0) + amount);
            }
            catCount = catCount.set(
              txn.coa.id,
              new CategorySummary({
                count,
                amount,
                amountByMonth,
                coa: txn.coa,
                acctId,
              }),
            );
          }
        }
      });
    });
    if (sortByAmount) {
      return catCount.toList().sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
    }
    return catCount.toList().sort((a, b) => b.count - a.count);
  },
)({
  // The result already aggregates every account.
  keySelector: (
    _state: unknown,
    props: { dateRange?: Interval; sortByAmount?: boolean; usageType?: string } | undefined,
    _selectorKey?: string,
  ) =>
    `${(props && props.dateRange && props.dateRange.toISO()) || 'nodate'}|${props && props.sortByAmount}|${props?.usageType || 'all'}`,
  cacheObject: new LruObjectCache({ cacheSize: 5 }),
});

export const getTransactionsByIds = createCachedSelector(
  getResourcesById,
  (_state: unknown, ids: ImList<string> | null | undefined) => ids,
  (transactionsById, ids) => {
    if (!ids || ids.size === 0 || !transactionsById?.size) {
      return ImList<TransactionRecord>();
    }
    const retArr = ids
      .map((id) => {
        if (!transactionsById.has(id)) {
          log.warn(`Transaction with id ${id} not found`);
          return undefined;
        }
        return transactionsById.get(id);
      })
      ?.filter(Boolean) as ImList<RecordOf<TransactionProps>>;

    return retArr.toList();
  },
)({
  keySelector: (_state: unknown, ids: ImList<string> | null | undefined, selectorKey?: string) =>
    (ids?.hashCode() ?? '') + (selectorKey ?? ''),
  cacheObject: new LruObjectCache({ cacheSize: 30 }),
});

export const getCategoryDisplayString = createSelector(
  [
    categoriesSelectors.getCategoriesById,
    accountsSelectors.getAccountsById,
    (_state: unknown, txn: TransactionProps | undefined) => txn,
    (_state: unknown, _txn: TransactionProps | undefined, noParent = true) => noParent,
  ],
  (categoriesById, accountsById, txn, noParent): string | undefined =>
    txn ? transactionsUtils.formatCategoryDisplayString(txn, categoriesById, accountsById, noParent) : undefined,
);

export const getTransactionsByTextFilter = createSelector(
  (
    state: State,
    props: {
      transactions?: ImMap<string, TransactionRecord> | null;
      accountIds?: string[] | ImList<string> | null;
    },
  ) => props.transactions || getTransactionsForAccountIds(state, props),
  accountsSelectors.getAccountsById,
  categoriesSelectors.getCategoriesById,
  tagsSelectors.getTags,
  (_state: unknown, props: { filter?: string | null }) => props.filter,
  (transactionsById, accountsById, categoriesById, tagsById, filter) => {
    const filterLowerCase = filter ? filter.toLowerCase() : undefined;
    const filterDate = filter ? Date.parse(filter) : undefined;
    let filterNumber = filter ? Number(filter) : undefined;
    filterNumber = Number.isNaN(filterNumber) ? undefined : Math.round(Math.abs(filterNumber as number));

    const transactionsByIdFiltered =
      !filterLowerCase || !filterLowerCase.length
        ? transactionsById
        : transactionsById.filter((transaction) => {
            let keepTransaction = false;

            if (!keepTransaction && transaction.payee && transaction.payee.toLowerCase().includes(filterLowerCase)) {
              keepTransaction = true;
            }
            if (!keepTransaction) {
              const account = accountsById.get(transaction.accountId || '');
              if (account && account.name?.toLowerCase().includes(filterLowerCase)) {
                keepTransaction = true;
              }
            }
            if (
              !keepTransaction &&
              searchItemForText(transaction, categoriesById, accountsById, filterLowerCase, tagsById, filterNumber)
            ) {
              keepTransaction = true;
            }
            if (!keepTransaction && transaction.split && transaction.split.items) {
              if (
                transaction.split.items.find((item) =>
                  searchItemForText(item, categoriesById, accountsById, filterLowerCase, tagsById),
                )
              ) {
                keepTransaction = true;
              }
            }
            if (!keepTransaction && filterDate && filterDate === Date.parse(transaction.postedOn)) {
              keepTransaction = true;
            }

            return keepTransaction;
          });

    return transactionsByIdFiltered;
  },
);

const searchItemForText = (
  item: SplitItemProps | TransactionProps,
  categoriesById: ReturnType<typeof categoriesSelectors.getCategoriesById>,
  accountsById: ReturnType<typeof accountsSelectors.getAccountsById>,
  filterText?: string,
  tagsById?: ReturnType<typeof tagsSelectors.getTags>,
  filterNumber?: number,
) => {
  let found = false;

  if (!found && filterText && item.memo && item.memo.toLowerCase().includes(filterText)) {
    found = true;
  }
  if (!found && filterText && 'name' in item && item.name && item.name.toLowerCase().includes(filterText)) {
    found = true;
  }
  if (!found && filterNumber !== undefined && Math.round(Math.abs(Number(item.amount ?? 0))) === filterNumber) {
    found = true;
  }
  if (!found) {
    const coaString = chartOfAccountsUtils.getCoaString(item.coa, categoriesById, accountsById, true);
    if (coaString && filterText && coaString.toLowerCase().includes(filterText)) {
      found = true;
    }
  }
  if (!found && item.tags && tagsById) {
    if (
      item.tags.find((tagRef) => {
        const tag = tagsById.get(tagRef.id || '');
        return Boolean(tag && filterText && tag.name?.toLowerCase().includes(filterText));
      })
    ) {
      found = true;
    }
  }

  return found;
};

export const isIncomeTxn = createSelector(
  (_state: unknown, txn: TransactionProps | null | undefined) => txn,
  categoriesSelectors.getIncomeCOAIds,
  transactionsUtils.isIncomeFromTxn,
);

// used for on-the-fly creation in transaction details
export const getCreatedTxnByClientAndAccountId = createSelector(
  (_state: unknown, clientId: string | undefined) => clientId,
  (state: State, _clientId: string | undefined, accountId: string) =>
    getTransactionsForAccountIds(state, { accountIds: [accountId] }),
  (clientId, txns) => {
    if (clientId) {
      return txns.find((item) => item.clientId === clientId && item.clientId !== item.id);
    }
    return undefined;
  },
);

export const getTxnByMatchedTxnAndAccountId = createSelector(
  (_state: unknown, matchedTxnId: string) => matchedTxnId,
  (state: State, _matchedTxnId: string, accountId: string) =>
    getTransactionsForAccountIds(state, { accountIds: [accountId] }),
  (matchedTxnId, txns) => {
    if (matchedTxnId) {
      return txns.find((item) => item.matchedTxn?.id === matchedTxnId);
    }
    return undefined;
  },
);

const getImmutableListFromIds = weakMapMemoize((ids?: string[]) => ImList<string>(ids));

// currently only used in F2S
export const getSortedTransactionsByIds = createWeakMapSelector(
  (state: State, ids: ImList<string> | string[] | undefined) =>
    getTransactionsByIds(state, ImList.isList(ids) ? ids : getImmutableListFromIds(ids)),
  (txns) =>
    txns?.sort((a: TransactionRecord, b: TransactionRecord) => {
      if (a?.postedOn >= b?.postedOn) {
        return 1;
      }
      return -1;
    }),
);

export const getTrackingProps = createSelector(
  (state: State | undefined, newTransaction: TransactionRecord) =>
    getTransactionById(state || store!.getState(), getId(newTransaction)),
  (_state: unknown, newTransaction: TransactionRecord) => newTransaction,
  (existingTransaction, newTransaction) => {
    const baseTransaction = (existingTransaction || newTransaction.clear())?.toJS?.() as { [key: string]: unknown };
    const fields = newTransaction.isDeleted
      ? ['isDeleted']
      : Object.entries(newTransaction.toJS()).reduce(
          (result, [key, value]) =>
            isEqualWith(value, baseTransaction?.[key], (obj1, obj2) => {
              const isNilOrEmptyList = (v: unknown) => v == null || (Array.isArray(v) && v.length === 0);
              return (
                isEqual(obj1, obj2) ||
                obj1 == null ||
                (!obj1 && !obj2) ||
                (isNilOrEmptyList(obj1) && isNilOrEmptyList(obj2))
              );
            })
              ? result
              : [...result, key],
          [] as string[],
        );

    let type;
    if (newTransaction?.id && newTransaction.id !== '0') {
      if (baseTransaction?.type === 'INVESTMENT') {
        type = 'investment txn';
      } else {
        type = baseTransaction.cpData ? 'edit downloaded' : 'edit manual';
      }
    } else {
      type = 'new manual';
    }

    return {
      investmentTxnType: newTransaction.investmentTxnType,
      state: 'complete',
      type,
      fields,
    };
  },
);

export const getSecuritiesUsage = createSelector(
  getSecuritiesResourcesById,
  (state: State) => getTransactionsOfTypes(state, TxnTypes.INVESTMENT_TXN),
  investmentHoldingsV2Selectors.getInvestmentHoldingNodes,
  (securities, transactions, investmentHoldingNodes) => {
    // FIXME: add types to investment holdings and remove the any
    const holdingsCount = securities.map((security) =>
      investmentHoldingNodes.reduce(
        (usageCount: number, invHolding: any) =>
          usageCount +
          (invHolding.holdings[0].localSecurityId === security.id || invHolding.name === security.name ? 1 : 0),
        0,
      ),
    );
    const txnsCount = securities.map((security) =>
      transactions.reduce(
        (usageCount, transaction) => usageCount + (transaction.localSecurityId === security.id ? 1 : 0),
        0,
      ),
    );
    const merged = securities.map((security) =>
      txnsCount.reduce(
        () => (txnsCount?.get(security.id || '') || 0) + (holdingsCount?.get(security.id || '') || 0),
        0,
      ),
    );
    return { merged, txnsCount, holdingsCount };
  },
);

export const getTxnsByIdInArAccounts = createSelector(
  [
    accountsSelectors.getAllArAccounts,
    (state: State, props?: { txnTypes?: number }) =>
      getTransactionsByAccountId(state, { txnTypes: props?.txnTypes ?? txnTypesDefault }),
  ],
  (arAccounts, txnsByAccountIds) => {
    if (!arAccounts || arAccounts.isEmpty()) {
      return ImMap<string, TransactionRecord>(); // Return empty map if no AR accounts
    }

    // Accumulate transactions from all AR accounts into a single map
    return arAccounts.reduce((acc, account) => {
      const accountTxns = txnsByAccountIds.get(getId(account), ImMap<string, TransactionRecord>());
      return acc.merge(accountTxns);
    }, ImMap<string, TransactionRecord>());
  },
);

export const getTxnsInArAccountsByCustomer = createSelector(getTxnsByIdInArAccounts, (txnsById) =>
  txnsById.groupBy((txn) => txn.customerId ?? 'no-customer'),
);

export const getTxnsInArAccountsByBusinessByCustomer = createSelector(getTxnsByIdInArAccounts, (txnsById) =>
  txnsById
    .groupBy((txn: TransactionRecord) => txn.businessId ?? 'no-business') // First group by businessId
    .map(
      (txnsByBusinessId) => txnsByBusinessId.groupBy((txn: TransactionRecord) => txn.customerId ?? 'no-customer'), // Then group by customerId within each businessId
    ),
);

export const getSalesReceiptsByCustomer = createSelector(getResourcesById, (transactions) =>
  transactions
    ?.filter((txn) => txn.type === 'INVOICE' && txn.subtype === TransactionSubType.SalesReceipt)
    ?.groupBy((txn) => txn.customerId ?? 'no-customer'),
);

export const getTxnsInArAccountsByProjectId = createSelector(getTxnsByIdInArAccounts, (txnsByIdInAr) =>
  txnsByIdInAr.groupBy((txn) => txn.projectId ?? (txn.customerId ? `${txn.customerId}-no-project` : 'no-project')),
);

export const getTxnsInArForProjectId = createWeakMapSelector(
  getTxnsInArAccountsByProjectId,
  (_state: unknown, projectId: string) => projectId,
  (txnsByProjectId, projectId) => txnsByProjectId.get(projectId) ?? ImList<TransactionRecord>(),
);

export const isAccountsReceivableTxn = createSelector(
  [(_state: unknown, props: { txn: TransactionRecord }) => props.txn, accountsSelectors.getAllArAccountIds],
  (txn, arAccountIds) => arAccountIds.includes(txn.accountId!),
);

// Examples: Payments, Credit-Memo, Bad Debt & Sales Tax txns
export const isQBPCreditTxn = createSelector(
  [(_state: unknown, props: { txn: TransactionProps }) => props.txn, accountsSelectors.getAccountsById],
  (txn, accounts) => {
    const account = txn.accountId ? accounts.get(txn.accountId) : null;
    return account && txn.type === 'CASH_FLOW' && isQBPAccount(account) && txn.amount < -0.005;
  },
);

// Examples: Payments, Credit-Memo, Bad Debt
export const isAccountsReceivableCreditTxn = createSelector(
  [(_state: unknown, props: { txn: TransactionProps }) => props.txn, accountsSelectors.getAllArAccountIds],
  (txn, arAccountIds) => txn.type === 'CASH_FLOW' && arAccountIds.includes(txn.accountId!) && txn.amount < -0.005,
);

// Examples: Payments, Unearned Income (transfer from A/R)
export const isOtherSideOfAccountsReceivableCreditTxn = createSelector(
  [(_state: unknown, props: { txn: TransactionRecord }) => props.txn, accountsSelectors.getAllArAccountIds],
  (txn, arAccountIds) =>
    txn.type === 'CASH_FLOW' && txn.coa?.type === 'ACCOUNT' && arAccountIds.includes(txn.coa.id) && txn.amount > 0.005,
);

export const isInvoicePaymentTransfer = createSelector(
  [isAccountsReceivableTxn, isOtherSideOfAccountsReceivableCreditTxn],
  (a, b) => a || b,
);

export const isInvoicePaymentSplit = createSelector(
  [getAccountsById, (_: unknown, args: { item: SplitItemProps }) => args.item],
  (accounts, item) => isInvoicePaymentSplitItem(item, accounts),
);

const sumUnpaidAmount = (sum: number, txn: TransactionRecord) => {
  if (transactionsUtils.isInvoiceTxn(txn)) {
    let paid = 0.0;
    if (Array.isArray(txn.allocations)) {
      paid = txn.allocations.reduce<number>(
        (s: number, allocation: AllocationProps) => s + (allocation.amount ?? 0),
        0,
      );
    } else if (txn.allocations instanceof ImList) {
      paid = txn.allocations.reduce<number>((s: number, allocation: Allocation) => s + (allocation.amount ?? 0), 0);
    }
    const unpaid = Number(txn.amount) - paid;
    return sum + unpaid;
  }
  return sum;
};

export const getBilledSumForProjectId = createWeakMapSelector(
  getTxnsInArForProjectId,
  (txns) => (txns as ImList<TransactionRecord>)?.reduce<number>(sumUnpaidAmount, 0) ?? 0,
);

export const getTxnsInArForCustomerId = createWeakMapSelector(
  getTxnsInArAccountsByCustomer,
  (_state: unknown, customerId: string) => customerId,
  (txnsByCustomerId, customerId) => txnsByCustomerId.get(customerId) ?? ImList<TransactionRecord>(),
);

// flattens transactions into split items list (TransactionItems)
export const getTransactionItemsById = createSelector(
  getResourcesById,
  (function combiner() {
    const transactionCache = new WeakMap<TransactionRecord, TransactionItem>();
    const transactionSplitCache = new WeakMap<SplitItemProps, TransactionItem>();
    return (transactions: ImMap<string, TransactionRecord>) => {
      return ImMap<string, TransactionItem>().withMutations((map) => {
        transactions.forEach((transaction, id) => {
          if (!assert(ImRecord.isRecord(transaction), 'transaction is not a record in getTransactionItemsById')) return;
          if (
            transaction?.split?.items &&
            (Array.isArray(transaction?.split?.items)
              ? transaction.split.items.length
              : transaction?.split?.items?.size)
          ) {
            transaction?.split?.items.forEach((splitItem, index) => {
              const stableSplitId = transactionsUtils.getStableSplitItemId(transaction, splitItem, index);

              // If there is a previously cached item for this split (ie no changes made) re-use the last one.
              // We still ensure the combined `item.id` matches the stable key used to index the flattened map.
              // Rebuild when the parent transaction reference changes too: the flattened `item` is derived
              // from `transaction.toObject()`, so a parent-only update (e.g. toggling isReviewed) would
              // otherwise return a stale split item since the splitItem reference is unchanged.
              let transactionItem = transactionSplitCache.get(splitItem);
              if (!transactionItem || transactionItem.transaction !== transaction) {
                transactionItem = mkTransactionItem({
                  transaction,
                  splitItem: splitItem as RecordOf<SplitItemProps>,
                  item: {
                    ...transaction?.toObject(),
                    ...(ImRecord.isRecord(splitItem) ? splitItem?.toObject() : splitItem),
                    id: stableSplitId,
                  },
                });
                transactionSplitCache.set(splitItem, transactionItem);
              } else if (transactionItem.item?.id !== stableSplitId) {
                transactionItem = transactionItem.set('item', { ...transactionItem.item, id: stableSplitId });
                transactionSplitCache.set(splitItem, transactionItem);
              }
              map.set(stableSplitId, transactionItem);
            });
          } else {
            const transactionItem =
              transactionCache.get(transaction) ?? mkTransactionItem({ transaction, item: transaction?.toObject() });
            if (!transactionCache.has(transaction)) {
              transactionCache.set(transaction, transactionItem);
            }
            map.set(id, transactionItem);
          }
        });
      });
    };
  })(),
);

export const getTransactionItemsByIdOfTypes = createSelector(
  [
    getTransactionItemsById,
    accountsSelectors.getGoalAccountIds,
    (_state: unknown, txnTypes: number | undefined) => txnTypes || txnTypesDefault,
    getCurrentDateString,
  ],
  (transactionItemsById, goalAccountIds, txnTypes, todayISO) =>
    transactionItemsById.filter((transactionItem) =>
      transactionOfTypes(transactionItem.transaction, goalAccountIds, txnTypes, todayISO),
    ),
);

type FlattenedItems = { version: number; size: number; items: ImMap<string, TransactionItem> };

// Without the same keys, the flattened map would order its entries differently, so only edits of
// existing transactions are applied in place; the change is logged for the scope filters below. Maps
// of up to 8 entries are reordered when they grow, so they are always rebuilt.
function updateChangedItems(
  transactions: ImMap<string, TransactionRecord>,
  previous: FlattenedItems,
): ImMap<string, TransactionItem> | undefined {
  if (previous.size <= 8 || transactions.size <= 8) return undefined;
  const changes = resourceChangeLog.getResourceChangesSince(transactions, previous.version);
  if (!changes || changes.removed) return undefined;
  let items = previous.items;
  const changed: string[] = [];
  for (const id of changes.keys) {
    const transaction = transactions.get(id);
    const item = items.get(id);
    if (!transaction || !ImRecord.isRecord(transaction) || !item) return undefined;
    if (item.transaction !== transaction) {
      items = items.set(id, mkTransactionItem({ transaction, item: transaction.toObject() }));
      changed.push(id);
    }
  }
  resourceChangeLog.logResourceChange(previous.items, items, changed, false);
  return items;
}

// todo - remove when main txn register shows splits as new line
// flattens transactions into TransactionItems for main register
export const getTransactionItemsByIdWithoutSplits = createSelector(
  getResourcesById,
  (function combiner() {
    let previous: FlattenedItems | undefined;
    registerCacheReset(() => {
      previous = undefined;
    });
    return (transactions: ImMap<string, TransactionRecord>) => {
      const transactionItemsByIdPrev = previous?.items;
      const transactionItemsById =
        (previous && updateChangedItems(transactions, previous)) ??
        ImMap<string, TransactionItem>(
          transactions
            ?.filter((transaction) =>
              assert(ImRecord.isRecord(transaction), 'transaction is not a record in getTransactionItemsById'),
            )
            ?.flatMap((transaction, id) => {
              const transactionItemPrev = transactionItemsByIdPrev?.get(id);

              // Check reference equality first - if transaction hasn't changed, reuse cached item
              if (transactionItemPrev && transactionItemPrev.transaction === transaction) {
                return [[id, transactionItemPrev]];
              }

              // Transaction changed or is new - create new TransactionItem
              const transactionItem = mkTransactionItem({ transaction, item: transaction?.toObject() });
              return [[id, transactionItem]];
            }),
        );
      previous = {
        version: resourceChangeLog.getResourceVersion(transactions),
        size: transactions.size,
        items: transactionItemsById,
      };
      return transactionItemsById;
    };
  })(),
);

export const getTransactionItemById = (state: State, id: string) => getTransactionItemsById(state).get(id);

// todo - remove when main txn register shows splits as new line
export const getTransactionItemByIdWithoutSplits = (state: State, id: string) =>
  getTransactionItemsByIdWithoutSplits(state).get(id);

// todo - remove when main txn register shows splits as new line
export const getTransactionItemsByIdsWithoutSplits = createCachedSelector(
  getTransactionItemsByIdWithoutSplits,
  (_state: State, transactionItemIds: ImSet<string> | null) => transactionItemIds,
  (transactionItemsById, transactionItemIds) =>
    transactionItemsById.filter((transactionItem) => transactionItemIds?.has(transactionItem.item.id as string)),
)({
  keySelector: (_state, transactionItemIds) => transactionItemIds?.hashCode() ?? 'unknown',
  cacheObject: new LruMapCache({ cacheSize: 10 }),
});

export const getTransactionItemsByIds = createCachedSelector(
  getTransactionItemsById,
  (_state: State, transactionItemIds: ImSet<string> | null) => transactionItemIds,
  (transactionItemsById, transactionItemIds) =>
    transactionItemsById.filter((transactionItem) => transactionItemIds?.has(transactionItem.item.id as string)),
)({
  keySelector: (_state, transactionItemIds) => transactionItemIds?.hashCode() ?? 'unknown',
  cacheObject: new LruMapCache({ cacheSize: 10 }),
});

export enum ProjectTransactionItemGroups {
  BALANCE_ADJUSTMENT = 'BALANCE_ADJUSTMENT',
  EXPENSE = 'EXPENSE',
  INCOME = 'INCOME',
  TRANSFER = 'TRANSFER',
  UNCATEGORIZED = 'UNCATEGORIZED',
}

// TODO: Should we exclude certain accounts or transaction types/subtypes?
//
// This selector groups transaction items by project ID and then by ProjectTransactionItemGroups. If item has
// a customerId and no projectId, it will be grouped by customerId (i.e. no-project customerId).
//
// Potential uses in downstream selectors:
//   On Client Page, sum for unbilled, billed and revenue
//   On Client Page, unbilled items and expenses (across projects)
//   On Project Page, sum for unbilled, billed, unpaid, non-billable, project value, paid
//   On Project Page, billable items, billed items and project expenses
//   On Invoice Form, need to add all billable expenses that haven't yet been billed
//
const getProjectTxnItemGroup = (coa: chartOfAccountsTypes.ChartOfAccount, categoriesById: ImMap<string, Category>) => {
  switch (coa.type) {
    case chartOfAccountsTypes.CoaTypeEnum.ACCOUNT:
      return ProjectTransactionItemGroups.TRANSFER;
    case chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT:
      return ProjectTransactionItemGroups.BALANCE_ADJUSTMENT;
    case chartOfAccountsTypes.CoaTypeEnum.CATEGORY: {
      const category = categoriesById.get(coa.id);
      if (!category) {
        return ProjectTransactionItemGroups.UNCATEGORIZED;
      }
      return category.type === 'INCOME' ? ProjectTransactionItemGroups.INCOME : ProjectTransactionItemGroups.EXPENSE;
    }
    case chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED:
      return ProjectTransactionItemGroups.UNCATEGORIZED;
    default:
      log.warn('Unexpected COA type', coa.type);
      return undefined;
  }
};

// Helper function to process transactions
const processTransactionItem = (
  projectId: string,
  txn: TransactionRecord,
  item: RecordOf<SplitItemProps> | null,
  categoriesById: ImMap<string, Category>,
  tempMap: Map<string, Map<`${ProjectTransactionItemGroups}`, TransactionItem[]>>,
) => {
  const coa = item ? item.coa : txn.coa;
  const coaGroup = coa ? getProjectTxnItemGroup(coa as chartOfAccountsTypes.ChartOfAccount, categoriesById) : undefined;
  if (!coaGroup) return;

  const txnItem = mkTransactionItem({
    transaction: txn,
    splitItem: item ? item : undefined,
    item: {
      ...txn.toObject(),
      ...(item ? item.toObject() : {}),
    },
  });

  if (!tempMap.has(projectId)) {
    tempMap.set(projectId, new Map<ProjectTransactionItemGroups, TransactionItem[]>());
  }
  const projectMap = tempMap.get(projectId)!;

  if (!projectMap.has(coaGroup)) {
    projectMap.set(coaGroup, []);
  }
  projectMap.get(coaGroup)?.push(txnItem);
};

// Convert tempMap to Immutable Map
const processTempMap = (tempMap: Map<string, Map<`${ProjectTransactionItemGroups}`, TransactionItem[]>>) => {
  return ImMap<string, ImMap<`${ProjectTransactionItemGroups}`, ImList<TransactionItem>>>().withMutations((map) => {
    tempMap.forEach((coaGroupMap, projectId) => {
      map.set(
        projectId,
        ImMap<`${ProjectTransactionItemGroups}`, ImList<TransactionItem>>().withMutations((coaMap) => {
          coaGroupMap.forEach((items, coaGroup) => {
            coaMap.set(coaGroup, ImList(items));
          });
        }),
      );
    });
  });
};

const groupTransactionItemGroupsByKey = (
  txnsById: ImMap<string, TransactionRecord>,
  categoriesById: ImMap<string, Category>,
  keyExtractor: (item: TransactionRecord | SplitItemProps) => string | undefined,
) => {
  // Temporary storage for project transactions grouped by coaGroup
  const tempMap = new Map<string, Map<`${ProjectTransactionItemGroups}`, TransactionItem[]>>();

  // Iterate over transactions
  txnsById.forEach((txn: TransactionRecord) => {
    if (txn.split?.items) {
      txn.split.items.forEach((splitItem) => {
        const item = splitItem as RecordOf<SplitItemProps>;
        const key = keyExtractor(item);
        if (key) {
          processTransactionItem(key, txn, item, categoriesById, tempMap);
        }
      });
    } else {
      const key = keyExtractor(txn);
      if (key) {
        processTransactionItem(key, txn, null, categoriesById, tempMap);
      }
    }
  });

  return processTempMap(tempMap);
};

export const getTransactionItemGroupsByProjectId = createSelector(
  [getTransactionsById, categoriesSelectors.getCategoriesById],
  (txnsById, categoriesById) => {
    return groupTransactionItemGroupsByKey(
      txnsById,
      categoriesById,
      (item) => (item.projectId || item.customerId) ?? undefined,
    );
  },
);
export const getTransactionItemGroupsByCustomerId = createSelector(
  [getTransactionsById, categoriesSelectors.getCategoriesById],
  (txnsById, categoriesById) => {
    return groupTransactionItemGroupsByKey(txnsById, categoriesById, (item) => item.customerId ?? undefined);
  },
);

const emptyTransactionItems = ImList<TransactionItem>();

type GetExpensesSelectorProps = {
  projectId?: string | null;
  customerId?: string | null;
  excludeNonBillable?: boolean;
  excludeBilled?: boolean;
  excludeUnbilled?: boolean;
};

const getExpenseTxnItems = (
  txnItemGroup?: ImMap<`${ProjectTransactionItemGroups}`, ImList<TransactionItem>>,
  excludeNonBillable?: GetExpensesSelectorProps['excludeNonBillable'],
  excludeUnbilled?: GetExpensesSelectorProps['excludeUnbilled'],
  excludeBilled?: GetExpensesSelectorProps['excludeBilled'],
) => {
  const expenses = txnItemGroup?.get(ProjectTransactionItemGroups.EXPENSE);
  const uncategorized = txnItemGroup
    ?.get(ProjectTransactionItemGroups.UNCATEGORIZED)
    ?.filter((txnItem) => txnItem.item.amount <= 0);

  if (!expenses && !uncategorized) return emptyTransactionItems;

  return (expenses || ImList<TransactionItem>())
    .concat(uncategorized || ImList<TransactionItem>())
    .filter((txnItem) => {
      // Include a cash flow txn, its splits (type normal), or a invoice txn split of type billable expense
      if (
        txnItem &&
        (txnItem.item.type === 'INVOICE_BILLABLE_EXPENSE' ||
          txnItem.item.type === 'CASH_FLOW' ||
          txnItem.item.type === 'NORMAL')
      ) {
        // `isLinkedBillableSplitItem` is broader than the old `isBilledItem`, but inclusion
        // is preserved: items without `linkedItem.id` fall through `isNonBilledItem` anyway.
        if (
          ((excludeNonBillable
            ? transactionsUtils.isNonBilledBillableItem(txnItem)
            : transactionsUtils.isNonBilledItem(txnItem)) ||
            (!excludeBilled && isLinkedBillableSplitItem(txnItem.item))) &&
          !(excludeUnbilled && transactionsUtils.isNonBilledBillableItem(txnItem))
        ) {
          return true;
        }
      }
      return false;
    });
};

export const getExpensesForProjectId = createSelector(
  [
    getTransactionItemGroupsByProjectId,
    (_state: any, props: GetExpensesSelectorProps) => props.projectId,
    (_state: any, props: GetExpensesSelectorProps) => props.customerId,
    (_state: any, props: GetExpensesSelectorProps) => props.excludeNonBillable,
    (_state: any, props: GetExpensesSelectorProps) => props.excludeUnbilled,
    (_state: any, props: GetExpensesSelectorProps) => props.excludeBilled,
  ],
  (txnItemGroups, projectId, customerId, excludeNonBillable, excludeUnbilled, excludeBilled) => {
    const id = projectId && projectId !== 'no-project' ? projectId : customerId;
    if (!id) return emptyTransactionItems;

    return getExpenseTxnItems(txnItemGroups.get(id), excludeNonBillable, excludeUnbilled, excludeBilled);
  },
);

export const selectUnbilledProjectExpenses = (state: any, { customerId, projectId }: GetExpensesSelectorProps) => {
  return getExpensesForProjectId(state, { customerId, projectId, excludeBilled: true, excludeNonBillable: true });
};

export const getExpensesForCustomerId = createSelector(
  [
    getTransactionItemGroupsByCustomerId,
    (_state: any, props: GetExpensesSelectorProps) => props.customerId,
    (_state: any, props: GetExpensesSelectorProps) => props.excludeNonBillable,
    (_state: any, props: GetExpensesSelectorProps) => props.excludeUnbilled,
    (_state: any, props: GetExpensesSelectorProps) => props.excludeBilled,
  ],
  (txnItemGroups, customerId, excludeNonBillable, excludeUnbilled, excludeBilled) => {
    if (!customerId) return emptyTransactionItems;

    return getExpenseTxnItems(txnItemGroups.get(customerId), excludeNonBillable, excludeUnbilled, excludeBilled);
  },
);

export const selectUnbilledCustomerExpenses = (state: any, { customerId }: GetExpensesSelectorProps) => {
  return getExpensesForCustomerId(state, { customerId, excludeBilled: true, excludeNonBillable: true });
};

export const getTransactionItemsFiltered = createCachedSelector(
  getTransactionItemsById,
  (_state: State, filterObject: filtersTypes.FilterObject) => filterObject,
  (transactionItemsById, filterObject) =>
    transactionItemsById.filter(transactionsUtils.createTransactionsFilterByObjFn(filterObject)),
)({
  keySelector: (_state, filterObject: filtersTypes.FilterObject) => filterObject?.hashCode(),
  cacheObject: new LruMapCache({ cacheSize: 10 }),
});

const getFilteredAccountIds = createSelector(
  (state: State, props: { accountIds: ImList<string> }) => props.accountIds,
  (state: State) => featureFlagsSelectors.getFeatureFlag(state, 'hideConnectedLoanTransactions'),
  (accountIds, hideConnectedLoanTransactions) =>
    ImList(
      accountIds.filter((accountId) =>
        store
          ? !accountsUtils.transactionsNotSupportedForAccountId(
              accountId,
              hideConnectedLoanTransactions,
              store?.getState(),
            )
          : true,
      ),
    ),
);

// TODO: Should we change behavior of transactionMatchesExpression when we have a split-item. Currently,
//       match will happen at the transaction level so all split-children will be matched if transaction
//       or any split-item matches.
//
const makeTransactionItemInScopePredicate = createCachedSelector(
  [
    (_state: State, props: { filterObject: filtersTypes.FilterObject }) => props.filterObject,
    accountsSelectors.getGoalAccountIds,
    getFilteredAccountIds, // reads props.accountIds internally
    dateSelectors.getCurrentDateString,
    (_state: State, props: { filterSearchText: string }) => props.filterSearchText,
  ],
  (filterObject, goalAccountIds, accountIds, todayString, filterSearchText) => {
    const parsedExpressions = filterSearchText ? getParsedExpressions(filterSearchText) : null;
    const accountIdSet = new Set(accountIds);
    const matchesFilterObject = transactionsUtils.createTransactionsFilterByObjFn(filterObject);

    return (txnItem: TransactionItem): boolean => {
      return (
        txnItem.transaction.postedOn <= todayString &&
        accountIdSet.has(txnItem.transaction.accountId || '') &&
        !transactionsUtils.isUnacceptedScheduledTxn(txnItem.transaction) &&
        matchesFilterObject(txnItem) &&
        isTransactionOfTypes(txnItem.transaction, goalAccountIds, txnTypesDefault, todayString) &&
        (!parsedExpressions || transactionMatchesExpression(txnItem.transaction, parsedExpressions))
      );
    };
  },
)({
  keySelector: (
    _state,
    props: {
      filterObject: filtersTypes.FilterObject;
      filterSearchText?: string;
      accountIds?: ImList<string>;
    },
  ) => {
    const filterHash = props.filterObject?.hashCode?.() ?? '0';
    const search = props.filterSearchText ?? '';
    const acctKey = Array.isArray(props.accountIds) ? props.accountIds.join(',') : 'all';
    return `${filterHash}|${search}|${acctKey}`;
  },
  cacheObject: new LruMapCache({ cacheSize: 10 }),
});

export const isTransactionItemInScope = createSelector(
  [makeTransactionItemInScopePredicate, (_state: State, _props: any, txnItem: TransactionItem) => txnItem],
  (isInScope, txnItem) => isInScope(txnItem),
);

// Scope-only predicate: like makeTransactionItemInScopePredicate, but drops
// the doesTxnMatchFilterObj (step 4) and transactionMatchesExpression (step 6)
// branches. Used by the MUI DataGrid filtering migration — see
// packages/web/app/components/Transactions/Register/FILTERING_MIGRATION.md.
const makeTransactionItemInScopePredicate_ScopeOnly = createCachedSelector(
  [
    accountsSelectors.getGoalAccountIds,
    getFilteredAccountIds, // reads props.accountIds internally
    dateSelectors.getCurrentDateString,
  ],
  (goalAccountIds, accountIds, todayString) => {
    const accountIdSet = new Set(accountIds);
    return (txnItem: TransactionItem): boolean => {
      return (
        txnItem.transaction.postedOn <= todayString &&
        accountIdSet.has(txnItem.transaction.accountId || '') &&
        !transactionsUtils.isUnacceptedScheduledTxn(txnItem.transaction) &&
        isTransactionOfTypes(txnItem.transaction, goalAccountIds, txnTypesDefault, todayString)
      );
    };
  },
)({
  keySelector: (_state, props: { accountIds?: ImList<string> }) =>
    Array.isArray(props.accountIds) ? props.accountIds.join(',') : 'all',
  cacheObject: new LruMapCache({ cacheSize: 10 }),
});

export const getTransactionItemsForRegisterScopeOnly = createCachedSelector(
  [getTransactionItemsById, makeTransactionItemInScopePredicate_ScopeOnly],
  (transactionItemsById, isInScope) => transactionItemsById.filter((transactionItem) => isInScope(transactionItem)),
)({
  keySelector: (_state, props: { accountIds?: ImList<string> }) =>
    Array.isArray(props.accountIds) ? props.accountIds.join(',') : 'all',
  cacheObject: new LruMapCache({ cacheSize: 10 }),
});

// Edits re-test only the items they changed. Each predicate is one cached scope, so it keys the filter
// state (re-reselect shares the result function across scopes).
const filterRegisterScope = selectorsHelpers.createIncrementalFilter(
  (transactionItemsById: ImMap<string, TransactionItem>, isInScope: (transactionItem: TransactionItem) => unknown) =>
    transactionItemsById.filter(isInScope),
);

export const getTransactionItemsWithoutSplitsForRegisterScopeOnly = createCachedSelector(
  [getTransactionItemsByIdWithoutSplits, makeTransactionItemInScopePredicate_ScopeOnly],
  (transactionItemsById, isInScope) => filterRegisterScope(transactionItemsById, [], isInScope, isInScope),
)({
  keySelector: (_state, props: { accountIds?: ImList<string> }) =>
    Array.isArray(props.accountIds) ? props.accountIds.join(',') : 'all',
  cacheObject: new LruMapCache({ cacheSize: 10 }),
});

export const getTransactionItemsForRegister = createCachedSelector(
  [getTransactionItemsById, makeTransactionItemInScopePredicate],
  (transactionItemsById, isInScope) => transactionItemsById.filter((transactionItem) => isInScope(transactionItem)),
)({
  keySelector: (
    _state,
    props: {
      filterObject: filtersTypes.FilterObject;
      filterSearchText?: string;
      accountIds?: ImList<string>;
    },
  ) => {
    const filterHash = props.filterObject?.hashCode?.() ?? '0';
    const search = props.filterSearchText ?? '';
    const acctKey = Array.isArray(props.accountIds) ? props.accountIds.join(',') : 'all';
    return `${filterHash}|${search}|${acctKey}`;
  },
  cacheObject: new LruMapCache({ cacheSize: 10 }),
});

export const getTransactionItemsWithoutSplitsForRegister = createCachedSelector(
  [getTransactionItemsByIdWithoutSplits, makeTransactionItemInScopePredicate],
  (transactionItemsById, isInScope) => {
    return transactionItemsById.filter((transactionItem) => isInScope(transactionItem));
  },
)({
  keySelector: (
    _state,
    props: {
      filterObject: filtersTypes.FilterObject;
      filterSearchText?: string;
      accountIds?: ImList<string>;
    },
  ) => {
    const filterHash = props.filterObject?.hashCode?.() ?? '0';
    const search = props.filterSearchText ?? '';
    const acctKey = Array.isArray(props.accountIds) ? props.accountIds.join(',') : 'all';
    return `${filterHash}|${search}|${acctKey}`;
  },
  cacheObject: new LruMapCache({ cacheSize: 10 }),
});

export const isTransactionItemReportableIncome = createSelector(
  [
    (_state: unknown, transactionItem: TransactionItemCombinedProps) => transactionItem,
    categoriesSelectors.getCategoriesById,
  ],
  (txn, categoriesById) => transactionsUtils.isTransactionItemReportableIncomeUtil(txn, categoriesById),
);

export const getTransactionStatusLabel = createSelector(
  [
    (_state: unknown, transactionItem: TransactionItemCombinedProps) => transactionItem,
    (_state: unknown, transactionItem: TransactionItemCombinedProps) => transactionItem.postedOn,
    dateSelectors.getCurrentDateString,
    (state: Pick<RootState, typeof ACCOUNTS_REDUCER_KEY>, transactionItem: TransactionItemCombinedProps) =>
      chartOfAccountsSelectors.isCreditBill(state, transactionItem.accountId, transactionItem.coa),
    isTransactionItemReportableIncome,
  ],
  (txn, postedOn, currentDay, isCredit, isIncome) => {
    const postedDate = DateTime.fromISO(postedOn).startOf('day');
    const pending = transactionsUtils.isUpcoming(txn, currentDay);
    const today = DateTime.fromISO(currentDay);

    const isTransfer = chartOfAccountsUtils.isTransferCOA(txn.coa);

    let myStatus = '';
    if (pending) {
      if (postedDate.hasSame(today, 'day')) {
        myStatus = StatusChipTypes.TODAY;
      } else if (postedDate > today) {
        myStatus = StatusChipTypes.UPCOMING;
      } else {
        myStatus = StatusChipTypes.PAST;
      }
    } else {
      myStatus = StatusChipTypes.PAID;
      if (isIncome) {
        myStatus = StatusChipTypes.RECEIVED;
      }
      if (isCredit) {
        myStatus = StatusChipTypes.PAID;
      }
      if (isTransfer) {
        // Sending
        if (txn.amount < 0) {
          myStatus = StatusChipTypes.SENT;
        }

        //  Receiving
        if (txn.amount > 0) {
          myStatus = StatusChipTypes.RECEIVED;
        }

        // Impossible
        if (txn.amount === 0) {
          myStatus = StatusChipTypes.SUCCESS;
        }
      }
    }
    return myStatus;
  },
);

export const getPotentialGoalTransactions = createSelector(
  [
    (state) => getTransactionsOfTypes(state, txnTypesCashAndPlanned),
    categoriesSelectors.getIncomeCOAIds,
    tagsSelectors.getTags,
    (_state, props) => props.filter,
    (_state, props) => props.withdrawMaximum,
    (_state, props) => props.accountId,
  ],
  (txns, incomeCOAIds, tags, filter, withdrawMaximum, accountId) => {
    return txns
      .filter(
        (txn) =>
          !incomeCOAIds.includes(txn.coa?.id ?? '') &&
          txn.accountId === accountId &&
          txn.type !== 'INVOICE' &&
          txn?.source !== 'SCHEDULED_TRANSACTION_PENDING' &&
          txn.amount > -1 * withdrawMaximum &&
          txn.amount < 0 &&
          txn.subtype !== 'GOAL_SPENT' &&
          txn?.coa?.type !== 'ACCOUNT' &&
          !txn?.split &&
          !txn.tags?.some((tag) => tags.get(tag.id ?? '')?.type === 'GOAL') &&
          (filter === '' || txn.payee?.toLowerCase().includes(filter.toLowerCase())),
      )
      .sortBy(
        ({ postedOn }) => postedOn,
        (a, b) => b.localeCompare(a),
      );
  },
);

// TODO Maybe: Make this work with splits if we ever get to that
export const getNonTransferTransactionItemsWithTagId = createSelector(
  [getTransactionItemsById, (state, tagId) => tagId],
  (transactions, tagId) =>
    transactions.filter(
      (transactionItem) =>
        transactionItem?.transaction?.tags?.some((tag) => tag?.id === tagId) &&
        transactionItem?.transaction?.coa?.type !== 'ACCOUNT',
    ),
);

const emptyMap = ImMap();

type GoalTxnsForIdAmountAndDateProps = {
  acctId: string;
  date: string;
  amount: number;
};
export const getGoalTxnsForIdAmountAndDate = createWeakMapSelector(
  [
    (state: State, props: GoalTxnsForIdAmountAndDateProps) => getTransactionsByAccountId(state).get(props.acctId),
    (state: State, props: GoalTxnsForIdAmountAndDateProps) => props.acctId,
    (state: State, props: GoalTxnsForIdAmountAndDateProps) => props.date,
    (_, props) => props.amount,
  ],
  (goalTxns, acctIdProp, date, amount) => {
    let filteredGoalTxns = emptyMap;
    if (goalTxns && goalTxns.size > 0) {
      filteredGoalTxns = goalTxns.filter((txn) => {
        // filter goal txns by amount (passed as property to selector)
        return txn.amount === amount && txn.postedOn === date;
      });
    }
    return filteredGoalTxns;
  },
);

type CreditAccount = RecordOf<accountsTypes.CreditAccountProps>;

export const getCreditAccountFromTxn = createSelector(
  [(_, txn?: TransactionProps | null) => txn, accountsSelectors.getAccountsById],
  (txn, accounts) => {
    if (!txn) return;
    const fromAccount = (txn?.accountId && accounts?.get(txn.accountId)) || undefined;
    const toAccount = (txn?.coa?.id && accounts?.get(txn.coa.id)) || undefined;
    if (toAccount && toAccount.type === 'CREDIT' && toAccount.isConnected) {
      return toAccount as CreditAccount;
    }
    if (fromAccount && fromAccount.type === 'CREDIT') {
      return fromAccount as CreditAccount;
    }
    // if coming from scheduledTransaction, maybe allow a provideable accountId for CC payments?
    return;
  },
);

// not using a memoizedSelector because it's just a boolean compare
export const getCanBVA = (state: State, txn: TransactionProps) => {
  if (getCreditAccountFromTxn(state, txn)) {
    return transactionsUtils.isTransferTxn(txn);
  }
  return false;
};

export const selectOtherTxnsForPanel = createSelector(
  [
    (state, props) => getTransactionItemGroupsByCustomerId(state).get(props.customerId),
    (_state, props) => props.projectId,
    getAccountsById,
  ],
  (txnItemsMap, projectId, accountsById) => {
    // want income transactions assigned to client/project
    const incomeItems = txnItemsMap?.get('INCOME') ?? ImList();
    const uncatItems = txnItemsMap?.get('UNCATEGORIZED')?.filter((txn) => txn?.item?.amount > 0) ?? ImList();

    return incomeItems
      ?.concat(uncatItems)
      .reduce((result: any[], txn: any) => {
        let projectCheck = true;
        if (projectId) {
          if (projectId !== 'no-project') {
            projectCheck = txn.item.projectId === projectId;
          } else {
            projectCheck = !txn.item.projectId;
          }
        }
        const account = accountsById.get(txn.item.accountId);

        if (
          txn?.item &&
          projectCheck &&
          txn.transaction.type !== 'INVOICE' &&
          txn.item.type !== 'INVOICE' &&
          account?.type !== 'ACCOUNTS_RECEIVABLE'
        ) {
          result.push(txn);
        }

        return result;
      }, [])
      .sort((a: any, b: any) => (a.item.postedOn < b.item.postedOn ? 1 : -1));
  },
);

export const getPreviousUnlinkedReminders = createSelector(
  [
    (state) => getTransactionsOfTypes(state, txnTypesCashAndPlanned),
    (_state, props) => props.stModelId,
    (_state, props) => props.postedOn,
  ],
  (txns, stModelId, postedOn) =>
    txns.reduce<RecordOf<TransactionProps>[]>((acc, txn) => {
      if (txn.source === 'SCHEDULED_TRANSACTION_PENDING' && txn.stModelId === stModelId && txn.postedOn < postedOn) {
        acc.push(txn);
      }
      return acc;
    }, []),
);

export const getSTModelTransactions = createSelector(
  getResourcesById,
  (state, stModelId) => stModelId,
  (transactions, stModelId) =>
    transactions.filter((transaction: TransactionRecord) => transaction.stModelId === stModelId),
);

type getSpendingDashTxnProps = {
  accountIds?: string[];
  exclude?: boolean;
  noIncome: boolean;
  // Business/Personal scope; pass a stable reference since this selector memoizes on identity.
  usageIds?: ReadonlySet<string>;
};

export const getSpendingDashTransactionCandidates = createWeakMapSelector(
  [
    (state: State, props?: getSpendingDashTxnProps) =>
      props?.accountIds ? getTransactionsForAccountIds(state, props) : getTransactionsById(state),
    dateSelectors.getCurrentDateString,
    accountsSelectors.getAccountsById,
    (state: State): boolean => featureFlagsSelectors.getFeatureFlags(state).get('hideConnectedLoanTransactions'),
    (_state: State, props) => props?.usageIds,
  ],
  (transactions, today, accounts, hideConnectedLoanTransactions, usageIds) =>
    transactions.filter(
      (txn) =>
        transactionsUtils.isExposedTransaction(txn, accounts, 'CASH_FLOW') &&
        !(hideConnectedLoanTransactions && transactionsUtils.isConnectedLoanTransaction(txn, accounts)) &&
        isPostedAndAcceptedTransaction(txn, today) &&
        transactionsUtils.isTransactionInUsages(txn, usageIds) &&
        // Cash-basis Spending Dashboard results do not include wholly unpaid invoices.
        !(transactionsUtils.isInvoiceTxn(txn) && Math.abs(transactionsUtils.sumAllocations(txn)) <= 0.005),
    ),
  { memoizeOptions: { resultEqualityCheck: immutableEntriesShallowEqual<string, TransactionRecord> } },
);

export const getSpendingDashTransactions = createWeakMapSelector(
  [
    getSpendingDashTransactionCandidates,
    categoriesSelectors.getIncomeCOAIds,
    (_state: State, props) => props?.exclude,
    (_state: State, props) => props?.noIncome,
  ],
  (transactions, incomeCOAs, exclude = true, noIncome = true) =>
    transactions.filter(
      (txn) =>
        // intentionally allow split income not to be dropped here, only drop non-split income - ignore bill + subscription flags
        !(noIncome && txn?.coa?.id && incomeCOAs?.has(txn.coa.id)) &&
        !(txn.isDeleted || (exclude && txn.isExcludedFromReports && !transactionsUtils.isSplitTxn(txn))),
    ),
);

type getSpendingDashTransactionsForPeriodProps = getSpendingDashTxnProps & {
  selectedPeriod: {
    startOn: string;
    endOn: string;
  };
};
export const getSpendingDashTransactionsForPeriod = createWeakMapSelector(
  [
    getSpendingDashTransactions,
    (_state: State, props: getSpendingDashTransactionsForPeriodProps) => props.selectedPeriod?.startOn,
    (_state: State, props: getSpendingDashTransactionsForPeriodProps) => props.selectedPeriod?.endOn,
  ],
  (transactions, startOn, endOn) => {
    if (!startOn || !endOn) {
      return transactions;
    }
    return transactions.filter((txn) => {
      return txn.postedOn >= startOn && txn.postedOn <= endOn;
    });
  },
);

type getTransactionItemsProps = {
  transactions: ImMap<string, RecordOf<TransactionProps>>;
  // filterFunction?: (txnItem: TransactionItem) => boolean;
};
export const getTransactionItemsForTransactions = createWeakMapSelector(
  [
    (props: getTransactionItemsProps) => props.transactions,
    // (props: getTransactionItemsProps) => props.filterFunction,
  ],
  (transactions) => {
    return ImMap<string, TransactionItem>()
      .withMutations((map) => {
        transactions?.forEach((transaction, id) => {
          if (!assert(ImRecord.isRecord(transaction), 'transaction is not a record in getTransactionItemsById')) return;
          if (
            transaction?.split?.items &&
            (Array.isArray(transaction?.split?.items)
              ? transaction.split.items.length
              : transaction?.split?.items?.size)
          ) {
            transaction?.split?.items.forEach((splitItem, index) => {
              const stableSplitId = transactionsUtils.getStableSplitItemId(transaction, splitItem, index);

              const transactionItem = mkTransactionItem({
                transaction,
                splitItem: splitItem as RecordOf<SplitItemProps>,
                item: {
                  ...transaction?.toObject(),
                  ...(ImRecord.isRecord(splitItem) ? splitItem?.toObject() : splitItem),
                  id: stableSplitId,
                },
              });
              map.set(stableSplitId, transactionItem);
            });
          } else {
            const transactionItem = mkTransactionItem({ transaction, item: transaction?.toObject() });
            map.set(id, transactionItem);
          }
        });
      })
      .valueSeq()
      .toList(); // return final object as List of Items
  },
);

// A store reset drops every key's selector (see selectorCacheRegistry).
registerCachedSelectors(
  getTransactionsOfTypes,
  getTransactionsForAccountIdSelectors,
  getCreditCardPaymentsByAccount,
  getCreditCardPaymentsTotal,
  getRefundActiveTransactionsByAccountId,
  getRefundActiveRelatedTxns,
  getRefundCompleteRelatedTxns,
  getRefundLinkEligibleTransactions,
  getTransactionsForAccountIds,
  getPostedTransactionsForAccountIds,
  getPopularCategorySuggestions,
  getMostPopularCatsByAccountId,
  getTransactionsByIds,
  getTransactionItemsByIdsWithoutSplits,
  getTransactionItemsByIds,
  getTransactionItemsFiltered,
  makeTransactionItemInScopePredicate,
  makeTransactionItemInScopePredicate_ScopeOnly,
  getTransactionItemsForRegisterScopeOnly,
  getTransactionItemsWithoutSplitsForRegisterScopeOnly,
  getTransactionItemsForRegister,
  getTransactionItemsWithoutSplitsForRegister,
);
